/**
 * The rule both in-page rails follow — the article contents list and the FAQ
 * category list.
 *
 * A link becomes current only once its target's top has crossed the line just
 * under the fixed header, never while the target is still down the page. The
 * line is measured rather than hard coded: the header is a different height per
 * breakpoint and is `position: fixed` once stuck, so the layout shifts under
 * it. It is never placed above the target's own `scroll-margin-top`, which is
 * where the browser parks a target when one of these links is clicked —
 * otherwise a click would land its own section just short of current.
 *
 * An IntersectionObserver answers a different question — whether the target is
 * on screen — which lights a link while its section is still halfway down the
 * viewport, and a long section whose heading has scrolled away stops counting.
 *
 * @param {HTMLElement[]} links   The rail's links, in document order.
 * @param {Object}        options Optional settings.
 * @param {number}        options.gap Space between the header and the line.
 * @param {Function}      options.resolve Maps a link to the element it marks.
 * @return {void}
 */
export function scrollSpy( links, options = {} ) {
	const {
		gap = 24,
		resolve = ( link ) => document.getElementById( decodeURIComponent( link.hash.slice( 1 ) ) ),
	} = options;

	// Each link is paired with its target once; a link whose target has gone is
	// dropped rather than looked up on every scroll.
	const items = links
		.map( ( link ) => ( { link, target: resolve( link ) } ) )
		.filter( ( item ) => item.target );

	if ( items.length < 2 ) {
		return;
	}

	const header = document.querySelector( '.site-header' );

	// Reading styles is expensive next to a rect, so the line is measured when
	// the page changes shape rather than on every scroll.
	let line = 0;

	const measure = () => {
		const headerHeight = header ? header.getBoundingClientRect().height : 0;
		const scrollMargin = parseFloat(
			window.getComputedStyle( items[ 0 ].target ).scrollMarginTop
		);

		line = Math.max( headerHeight + gap, scrollMargin || 0 );
	};

	let current = -1;

	const update = () => {
		// The last target whose top has crossed the line. Above the first one
		// the rail keeps pointing at it, as the server rendered it.
		let found = 0;

		items.forEach( ( item, i ) => {
			if ( item.target.getBoundingClientRect().top <= line + 1 ) {
				found = i;
			}
		} );

		if ( found === current ) {
			return;
		}

		current = found;

		items.forEach( ( item, i ) => item.link.classList.toggle( 'is-current', i === found ) );
	};

	// The work is one rect read per target, so the scroll handler runs it
	// directly. Coalescing into a requestAnimationFrame is cheaper in theory,
	// but a frame that never arrives — a throttled or hidden tab — leaves the
	// gate shut and the rail then ignores every scroll after it.
	const onResize = () => {
		measure();
		update();
	};

	window.addEventListener( 'scroll', update, { passive: true } );
	window.addEventListener( 'resize', onResize );

	// Media above the rail settles after load and moves every target with it.
	window.addEventListener( 'load', onResize );

	measure();
	update();
}
